// Scaffold Mode — the PROACTIVE entry point: a student opens this on purpose
// to revisit earlier chapters/topics and restart from the basics, independent
// of any wrong attempt. (The REACTIVE path — triggered from a wrong attempt,
// already knowing its subtopicId — is unchanged: see PracticeAnswerCard's
// onScaffold and TaskCard's "Scaffold from Zero" action, both of which still
// navigate straight to /learn/basics/:subtopicId.)
//
// Flow: pick a subject → see its topics (ordered by curriculum sequence) →
// pick a subtopic → land on the existing /learn/basics/:subtopicId level engine,
// completely unchanged. When arriving via ?fromTopicId= (a deep link from
// Practice Mode), topics that precede that topic are grouped under
// "Strengthen the basics first".
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { boardGradeLabel, decodeClaims } from '@/lib/auth';
import type { SubjectDTO, ScaffoldTopic } from '@/types';
import { BAND_FILL, BAND_LABEL } from '@/lib/bins';
import { StudentPage, StudentPageHeader } from '@/components/ds';

export function ScaffoldModePage() {
  const navigate = useNavigate();
  const claims = decodeClaims();
  const [searchParams] = useSearchParams();
  const fromTopicId = searchParams.get('fromTopicId') ?? undefined;
  const [subjectId, setSubjectId] = useState<string | null>(null);

  const subjectsQuery = useQuery({
    queryKey: ['subjects', claims?.board, claims?.grade],
    enabled: Boolean(claims?.board && claims?.grade),
    queryFn: () => apiGet<SubjectDTO[]>(`/subjects?board=${claims!.board}&grade=${claims!.grade}`),
  });

  const topicsQuery = useQuery({
    queryKey: ['scaffold-topics', subjectId, fromTopicId],
    enabled: Boolean(subjectId),
    queryFn: () =>
      apiGet<ScaffoldTopic[]>(
        `/scaffold/topics?subjectId=${subjectId}${fromTopicId ? `&fromTopicId=${fromTopicId}` : ''}`,
      ),
  });

  const { prerequisites, others } = useMemo(() => {
    const topics = topicsQuery.data ?? [];
    return {
      prerequisites: topics.filter((t) => t.isPrerequisite),
      others: topics.filter((t) => !t.isPrerequisite),
    };
  }, [topicsQuery.data]);

  const subjects = subjectsQuery.data ?? [];
  const selectedSubject = subjects.find((subject) => subject.id === subjectId);

  return (
    <StudentPage>
      <StudentPageHeader
        breadcrumbs={
          subjectId
            ? [
                { label: 'Learn from the basics', onClick: () => setSubjectId(null) },
                { label: selectedSubject?.name ?? 'Subject' },
              ]
            : undefined
        }
        title={selectedSubject?.name ?? 'Learn from the basics'}
        subtitle={`${boardGradeLabel(claims)} · Go back to basics, on your terms`}
      />

      <div className="w-full">
        {!subjectId ? (
          <>
            <h2 className="mb-3 text-sm font-bold" style={{ color: 'var(--text-body)' }}>
              Pick a subject
            </h2>
            {subjectsQuery.isLoading ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-20 animate-pulse rounded-2xl bg-paper-sunk" />
                ))}
              </div>
            ) : subjects.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                No subjects available yet.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {subjects.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSubjectId(s.id)}
                    className="rounded-2xl border border-line bg-card p-4 text-left font-semibold text-ink shadow-sm transition hover:border-forest"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {topicsQuery.isLoading ? (
              <div className="flex flex-col gap-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-16 animate-pulse rounded-2xl bg-paper-sunk" />
                ))}
              </div>
            ) : (topicsQuery.data ?? []).length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                No scaffold content available for this subject yet.
              </p>
            ) : (
              <div className="flex flex-col gap-5">
                {prerequisites.length > 0 && (
                  <TopicGroup
                    title="Strengthen the basics first"
                    hint="These come before what you're currently working on."
                    topics={prerequisites}
                    onPick={(subtopicId) => navigate(`/learn/basics/${subtopicId}`)}
                  />
                )}
                <TopicGroup
                  title={prerequisites.length > 0 ? 'Other topics' : 'Pick a topic'}
                  topics={others}
                  onPick={(subtopicId) => navigate(`/learn/basics/${subtopicId}`)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </StudentPage>
  );
}

function TopicGroup({
  title,
  hint,
  topics,
  onPick,
}: {
  title: string;
  hint?: string;
  topics: ScaffoldTopic[];
  onPick: (subtopicId: string) => void;
}) {
  if (topics.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
        {title}
      </h3>
      {hint && (
        <p className="mb-2 text-xs" style={{ color: 'var(--ink-3)' }}>
          {hint}
        </p>
      )}
      <div className="mt-2 flex flex-col gap-2">
        {topics.map((t) => (
          <div
            key={t.topicId}
            className="rounded-2xl p-3"
            style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
          >
            <div className="mb-2 flex items-center gap-2">
              <span className="font-semibold" style={{ color: 'var(--text-strong)' }}>
                {t.name}
              </span>
              {t.confidenceBand && (
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: BAND_FILL[t.confidenceBand] }}
                  title={BAND_LABEL[t.confidenceBand]}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {t.subtopics.map((st) => (
                <button
                  key={st.subtopicId}
                  onClick={() => onPick(st.subtopicId)}
                  className="rounded-full px-3 py-1 text-xs font-semibold transition"
                  style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
                >
                  {st.name}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
